/**
 * CANONICAL MODULE — app-platform/shared/storageScope.ts
 *
 * Copied byte-for-byte into every app (`src/lib/storageScope.ts`). Pure: no IndexedDB, no MSAL, no
 * app import. Built for work order W12 (task 2.b) from
 * [design/W12-storage.md](../design/W12-storage.md) §3.1.
 *
 * **Fix bugs HERE, then copy outward. Never edit an app's copy in place.**
 *
 * ── The invariant ────────────────────────────────────────────────────────────────────────────────
 * No byte of a store owned by one account is ever shown to, or written to OneDrive under, another
 * account. Three things enforce it:
 *
 * 1. **The store is bound to its account.** The data layer is built from a `StoreScope` and nothing
 *    else, and the sync engine's account getter is `boundAccountGetter(scope, …)`: it returns the
 *    SCOPE's account from the MSAL cache, never the active one. If the active account changes under
 *    a running tab, `onAccountChanged` fires (the app stops its engine and reloads), and the token
 *    still never becomes the other account's.
 * 2. **Scoped names after cutover.** `bindr@<homeAccountId>`; user-data localStorage keys take the
 *    same suffix (`trakr:draft@<id>:<date>`). Device preferences stay unscoped.
 * 3. **Legacy data is adopted in place, with proof** (`decideStore`): the pre-W12 store keeps its
 *    name; one owner-stamp `put` is the commit point. The proof ladder, first match wins:
 *
 *      P1 an owner stamp in the store          equal → open/adopt; different → never opened
 *      P2 session continuity                   "already signed in at first boot of the W12 build,
 *                                               no interactive sign-in since" (decision 2)
 *      P3 remote overlap                       ≥ half the legacy store's SYNCED units are in this
 *                                               account's listing, or any stored eTag matches;
 *                                               a non-empty remote with zero overlap → someone else's
 *      P4 nothing provable                     HOLD: a fresh scoped store; the legacy store is not
 *                                               opened, synced or deleted, and the user is asked
 *                                               (decision 1)
 *
 *    **MSAL's account list is deliberately not a proof, and not an option.** `logoutRedirect` drops
 *    the account from the cache, so after "A signs out, B signs in" the cache holds only B — the
 *    exact case where adopting would hand A's unsynced work and tombstones to B. `cachedAccounts`
 *    is accepted in the evidence only so a caller cannot mistake its absence for an oversight; it is
 *    never read.
 *
 * ── The open decisions are policies, not code paths ──────────────────────────────────────────────
 * Decisions 1 and 2 of W12 §7 are one-way doors that Kamrin has not yet settled. Each is a named
 * option on `ScopePolicy`, defaulting to the SAFE answer the design recommends:
 *
 *   unprovableOwner:    'hold-and-ask' (default) | 'adopt-first-sign-in'
 *   sessionContinuity:  'first-boot-signed-in' (default, exactly as defined) | 'never'
 *
 * There is no option that widens P2 to "the only MSAL account": that is the sign-out trap.
 */

/** How a store's ownership was established. */
export type ProofKind =
  | 'owner-stamp' // P1
  | 'session-continuity' // P2
  | 'remote-overlap' // P3
  | 'user-claimed' // "These are mine" from the held-data screen
  | 'unproven-first-sign-in' // only under unprovableOwner: 'adopt-first-sign-in' (NOT the default)
  | 'new-store' // a scoped store this build created for the account

export interface StoreScope {
  /** MSAL `homeAccountId`, verbatim. */
  readonly accountId: string
  /** The IndexedDB name (or key namespace) this account's data lives under. */
  readonly storeName: string
  /** True when this is the pre-W12 store adopted in place (its name is the bare base). */
  readonly legacy: boolean
}

/** The stamp written into a store: the adoption's commit point. One atomic single-row put. */
export interface OwnerStamp {
  key: 'owner'
  accountId: string
  /** ISO time it was written. */
  at: string
  proof: ProofKind
}

export interface ScopePolicy {
  /** W12 decision 1. What happens to pre-W12 data whose owner cannot be proven. */
  unprovableOwner: 'hold-and-ask' | 'adopt-first-sign-in'
  /** W12 decision 2. Whether P2 counts, and only ever as "already signed in at first boot". */
  sessionContinuity: 'first-boot-signed-in' | 'never'
  /** P3: the share of the legacy store's synced units that must be in the remote listing. */
  overlapThreshold: number
}

/**
 * The recommended answers to decisions 1 and 2: the safe defaults.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export const DEFAULT_SCOPE_POLICY: Readonly<ScopePolicy> = Object.freeze({
  unprovableOwner: 'hold-and-ask',
  sessionContinuity: 'first-boot-signed-in',
  overlapThreshold: 0.5,
})

export interface OwnershipEvidence {
  /**
   * P2. Present only when the FIRST boot of the W12 build found exactly one MSAL account ALREADY
   * signed in (from before this build), and no interactive sign-in has happened since. The app
   * records this once, at that first boot, before any sign-in UI can run. `null` otherwise.
   */
  signedInAtFirstBoot?: { accountId: string; interactiveSince: boolean } | null
  /**
   * P3, gathered after sign-in, against THIS account's remote listing. `syncedLocal` counts the
   * legacy store's units that were ever synced (non-dirty rows, `syncBase`, rows with an eTag);
   * never-synced rows prove nothing and are not counted.
   */
  remote?: {
    remoteCount: number
    syncedLocal: number
    /** Of `syncedLocal`, how many ids/paths exist in this account's listing. */
    overlapping: number
    /** Any stored eTag equals an eTag in this account's listing (eTags are per drive item). */
    eTagMatch: boolean
  } | null
  /** NEVER a proof (see the header). Accepted only so callers do not pass it somewhere worse. */
  cachedAccounts?: readonly string[]
}

export interface LegacyStoreState {
  /** The stamp's accountId (or quil's `settings.oneDriveUserId`), or null when unstamped. */
  owner: string | null
  hasData: boolean
  /** Dirty rows, queued writes and open conflicts: work that exists nowhere else. */
  unsynced: number
}

export type StoreDecision =
  | { kind: 'open-scoped'; name: string }
  | { kind: 'adopt-legacy'; name: string; stamp: string; why: ProofKind }
  | { kind: 'open-legacy'; name: string }
  | { kind: 'new-scoped'; name: string; legacyBelongsTo: string | null }
  | { kind: 'hold'; name: string; legacyUnclaimed: true }

// An account id never holds `@`, `:` or `~`: `@` joins a name to its id, and `:` and `~` are the
// delimiters that may follow one (`SCOPE_DELIMITERS`, in eraseDevice.ts, where they are used).
const ID_FORBIDDEN = /[@:~]/

/** An account id usable in a scoped name: non-empty, and none of `@ : ~` (MSAL's `oid.tid` never has them). */
export function isScopableAccountId(accountId: string | null | undefined): accountId is string {
  return typeof accountId === 'string' && accountId.length > 0 && !ID_FORBIDDEN.test(accountId)
}

function requireAccountId(accountId: string, what: string): void {
  if (!accountId) throw new Error(`storageScope: ${what} needs an account id`)
  if (ID_FORBIDDEN.test(accountId)) throw new Error(`storageScope: account id "${accountId}" must not contain "@", ":" or "~"`)
}

/** `${base}@${accountId}`. The only way to name an account's store. */
export function scopedStoreName(base: string, accountId: string): string {
  if (!base) throw new Error('storageScope: a store needs a base name')
  requireAccountId(accountId, 'a scoped store')
  if (/[@~]/.test(base)) throw new Error(`storageScope: base "${base}" must not contain "@" or "~"`)
  return `${base}@${accountId}`
}

/**
 * The account a scoped name belongs to, or null for a bare (legacy), foreign or malformed name.
 * Malformed (an id part containing `@` or `:`) is null, never a guess: a caller comparing the
 * result to an account id then treats the name as someone else's.
 */
export function accountOfStoreName(base: string, name: string): string | null {
  const prefix = `${base}@`
  if (!name.startsWith(prefix)) return null
  const rest = name.slice(prefix.length)
  // A migration backup of a scoped store is `${base}@${id}~pre-v7`: it belongs to the same account.
  const tilde = rest.indexOf('~')
  const id = tilde < 0 ? rest : rest.slice(0, tilde)
  return isScopableAccountId(id) ? id : null
}

/**
 * A user-data localStorage key for an account: `scopedKey('trakr:draft', id, '2026-10-08')` is
 * `trakr:draft@<id>:2026-10-08`. Device preferences never go through this.
 */
export function scopedKey(prefix: string, accountId: string, rest?: string): string {
  requireAccountId(accountId, 'a scoped key')
  if (!prefix || prefix.includes('@')) throw new Error(`storageScope: key prefix "${prefix}" must be non-empty and must not contain "@"`)
  return rest === undefined ? `${prefix}@${accountId}` : `${prefix}@${accountId}:${rest}`
}

/** The W11 lock for a store: one lock per local database (`${app}:${storeName}:sync`). */
export function syncLockName(app: string, scope: StoreScope): string {
  return `${app}:${scope.storeName}:sync`
}

export function ownerStamp(accountId: string, proof: ProofKind, at: string): OwnerStamp {
  return { key: 'owner', accountId, at, proof }
}

/** Read a stamp from whatever the store held (a meta row, a kv value, a parsed JSON string). */
export function parseOwnerStamp(raw: unknown): OwnerStamp | null {
  let v: unknown = raw
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v)
    } catch {
      return null
    }
  }
  if (!v || typeof v !== 'object') return null
  const o = v as Record<string, unknown>
  if (o.key !== 'owner' || typeof o.accountId !== 'string' || !o.accountId || typeof o.at !== 'string') return null
  const proof = typeof o.proof === 'string' ? (o.proof as ProofKind) : 'owner-stamp'
  return { key: 'owner', accountId: o.accountId, at: o.at, proof }
}

function provenByOverlap(e: OwnershipEvidence['remote'], threshold: number): 'mine' | 'theirs' | 'unknown' {
  if (!e) return 'unknown'
  if (e.eTagMatch) return 'mine'
  if (e.syncedLocal > 0 && e.overlapping / e.syncedLocal >= threshold) return 'mine'
  if (e.remoteCount > 0 && e.syncedLocal > 0 && e.overlapping === 0) return 'theirs'
  return 'unknown'
}

/**
 * Which store this account opens, and whether the legacy store is adopted. Pure; the app gathers
 * the inputs and performs the result (the stamp put is `adopt-legacy`'s commit point).
 */
export function decideStore(input: {
  base: string
  accountId: string
  scopedExists: boolean
  legacy: LegacyStoreState | null
  evidence: OwnershipEvidence
  policy?: Partial<ScopePolicy>
}): StoreDecision {
  const policy: ScopePolicy = { ...DEFAULT_SCOPE_POLICY, ...input.policy }
  const { base, accountId, legacy, evidence } = input
  const scoped = scopedStoreName(base, accountId)
  const ownStore = (legacyBelongsTo: string | null): StoreDecision =>
    input.scopedExists ? { kind: 'open-scoped', name: scoped } : { kind: 'new-scoped', name: scoped, legacyBelongsTo }

  // Nothing to adopt: no legacy store, or an empty unstamped one.
  if (!legacy || (!legacy.hasData && legacy.owner === null)) return ownStore(null)

  // P1: a stamp decides outright. A different owner's store is never opened.
  if (legacy.owner !== null) {
    return legacy.owner === accountId ? { kind: 'open-legacy', name: base } : ownStore(legacy.owner)
  }

  const overlap = provenByOverlap(evidence.remote, policy.overlapThreshold)

  // Unstamped legacy data, but this account already has its own store: the legacy store stays held
  // (it may be another account's). Only an explicit "These are mine" merges it. Unless P3 has
  // already shown it IS another account's: then it is not held from this account, just not its
  // own, and asking on every boot would offer to claim data that provably belongs to someone else
  // (found in contently's A2 adoption, 2026-10-09).
  if (input.scopedExists) {
    return overlap === 'theirs' ? { kind: 'open-scoped', name: scoped } : { kind: 'hold', name: scoped, legacyUnclaimed: true }
  }

  // P2: session continuity, exactly as defined (decision 2). Never the MSAL account list.
  const firstBoot = evidence.signedInAtFirstBoot
  if (
    policy.sessionContinuity === 'first-boot-signed-in' &&
    firstBoot &&
    firstBoot.accountId === accountId &&
    !firstBoot.interactiveSince
  ) {
    return { kind: 'adopt-legacy', name: base, stamp: accountId, why: 'session-continuity' }
  }

  // P3: remote overlap (computed above).
  if (overlap === 'mine') return { kind: 'adopt-legacy', name: base, stamp: accountId, why: 'remote-overlap' }
  if (overlap === 'theirs') return { kind: 'new-scoped', name: scoped, legacyBelongsTo: null }

  // P4: unprovable (decision 1). The default holds and asks.
  if (policy.unprovableOwner === 'adopt-first-sign-in') {
    return { kind: 'adopt-legacy', name: base, stamp: accountId, why: 'unproven-first-sign-in' }
  }
  return { kind: 'hold', name: scoped, legacyUnclaimed: true }
}

/** The `StoreScope` a decision opens. */
export function scopeFromDecision(accountId: string, d: StoreDecision): StoreScope {
  const legacy = d.kind === 'adopt-legacy' || d.kind === 'open-legacy'
  return { accountId, storeName: d.name, legacy }
}

/**
 * The account getter for the sync engine's token getter (`createTokenGetter({ getAccount })`): the
 * SCOPE's account, looked up in the MSAL cache by `homeAccountId`, never the active account. If the
 * active account is not the scope's, `onAccountChanged` is told (once per change) so the app can
 * stop its engine and reload; the getter still never returns the other account. If the scope's
 * account has left the cache (signed out), it returns `null`, which the token getter turns into
 * `InteractiveSignInRequiredError`: the store's work waits for its owner.
 */
export function boundAccountGetter<A extends { homeAccountId: string }>(
  scope: StoreScope,
  deps: {
    accounts: () => readonly A[]
    active: () => A | null
    onAccountChanged?: (activeAccountId: string | null) => void
  },
): () => A | null {
  let reported: string | null | undefined
  return () => {
    const active = deps.active()
    const activeId = active ? active.homeAccountId : null
    if (activeId !== scope.accountId && reported !== activeId) {
      reported = activeId
      try {
        deps.onAccountChanged?.(activeId)
      } catch {
        /* a listener is a consumer, never a dependency */
      }
    } else if (activeId === scope.accountId) reported = undefined
    return deps.accounts().find((a) => a.homeAccountId === scope.accountId) ?? null
  }
}

/** Throw unless `accountId` is the scope's: a guard for any code path that writes under a token. */
export function assertScopeAccount(scope: StoreScope, accountId: string | null | undefined): void {
  if (accountId !== scope.accountId) {
    throw new ScopeMismatchError(scope, accountId ?? null)
  }
}

export class ScopeMismatchError extends Error {
  readonly scope: StoreScope
  readonly accountId: string | null
  constructor(scope: StoreScope, accountId: string | null) {
    super(`storageScope: ${scope.storeName} belongs to another account; nothing was sent.`)
    this.scope = scope
    this.accountId = accountId
    this.name = 'ScopeMismatchError'
  }
}

// ------------------------------------------------------------------------------ key adoption

/** A `localStorage`-shaped store with enumeration. */
export interface EnumerableKv {
  readonly length: number
  key(index: number): string | null
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export interface KeyAdoptionReport {
  copied: number
  /** Scoped keys that already existed (a re-run after a kill, or newer scoped data): left as they are. */
  kept: number
  removed: number
}

/**
 * Adopt user-data localStorage keys into an account's scope (trakr's drafts): **copy all, then
 * stamp, then delete the originals**, each step idempotent, so a kill at any point converges on a
 * re-run with nothing lost and nothing doubled. A scoped key that already exists is never
 * overwritten (it is either the same bytes from an earlier copy, or newer work).
 */
export function adoptKeysInPlace(
  kv: EnumerableKv,
  opts: {
    /** Is this a legacy (unscoped) user-data key? */
    isLegacyKey: (key: string) => boolean
    /** Its scoped name for the adopting account. */
    scopedKeyFor: (key: string) => string
    ownerKey: string
    stamp: OwnerStamp
  },
): KeyAdoptionReport {
  const legacy: string[] = []
  for (let i = 0; i < kv.length; i += 1) {
    const k = kv.key(i)
    if (k !== null && opts.isLegacyKey(k)) legacy.push(k)
  }
  let copied = 0
  let kept = 0
  for (const k of legacy) {
    const value = kv.getItem(k)
    if (value === null) continue
    const target = opts.scopedKeyFor(k)
    if (target === k) throw new Error(`storageScope: "${k}" maps onto itself`)
    if (kv.getItem(target) !== null) kept += 1
    else {
      kv.setItem(target, value)
      copied += 1
    }
  }
  // The commit point. Before it, the originals are untouched; after it, they are copies.
  kv.setItem(opts.ownerKey, JSON.stringify(opts.stamp))
  let removed = 0
  for (const k of legacy) {
    if (kv.getItem(opts.scopedKeyFor(k)) !== null) {
      kv.removeItem(k)
      removed += 1
    }
  }
  return { copied, kept, removed }
}

/** What the "data from before account separation" screen shows for a held legacy store. */
export interface HeldLegacyNotice {
  title: string
  body: string
  actions: { id: 'claim' | 'export' | 'delete'; label: string; destructive: boolean }[]
}

export function describeHeldLegacy(input: { items: number; unsynced: number; unitNoun: string }): HeldLegacyNotice {
  const n = input.items
  const noun = n === 1 ? input.unitNoun : `${input.unitNoun}s`
  const unsynced =
    input.unsynced > 0 ? ` ${input.unsynced} of them were never synced, so this device holds the only copy.` : ''
  return {
    title: 'Data from before account separation',
    body: `This device holds ${n} ${noun} from before accounts were kept apart. We could not tell which account they belong to, so nothing has been opened or synced.${unsynced}`,
    actions: [
      { id: 'claim', label: 'These are mine', destructive: false },
      { id: 'export', label: 'Export them', destructive: false },
      { id: 'delete', label: 'Delete them', destructive: true },
    ],
  }
}
